import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  TreeStructure, Plus, Pencil, Trash, Upload, MagnifyingGlass, CaretRight
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [brandDialogOpen, setBrandDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkBrandDialogOpen, setBulkBrandDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);
  const [bulkCsv, setBulkCsv] = useState('');
  const [bulkBrandCsv, setBulkBrandCsv] = useState('');
  const [catForm, setCatForm] = useState({ name: '', parent_id: '', level: 1, description: '', commission_rate: 0, sort_order: 0 });
  const [brandForm, setBrandForm] = useState({ name: '', description: '', requires_authorization: false });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [catRes, brandRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/brands`, { withCredentials: true }),
      ]);
      setCategories(catRes.data);
      setBrands(brandRes.data);
    } catch (e) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const l1Categories = useMemo(() => categories.filter(c => c.level === 1), [categories]);

  const buildTree = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.category_id] = { ...c, children: [] }; });
    const roots = [];
    categories.forEach(c => {
      if (c.parent_id && map[c.parent_id]) {
        map[c.parent_id].children.push(map[c.category_id]);
      } else if (!c.parent_id) {
        roots.push(map[c.category_id]);
      }
    });
    return roots;
  }, [categories]);

  const handleSaveCat = async () => {
    try {
      const payload = { ...catForm, level: catForm.parent_id ? 2 : 1, commission_rate: parseFloat(catForm.commission_rate) || 0, sort_order: parseInt(catForm.sort_order) || 0 };
      if (!payload.parent_id) delete payload.parent_id;
      if (editingCat) {
        await axios.patch(`${API_URL}/api/admin/categories/${editingCat.category_id}`, payload, { withCredentials: true });
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/api/admin/categories`, payload, { withCredentials: true });
        toast.success('Category created');
      }
      setCatDialogOpen(false);
      setEditingCat(null);
      setCatForm({ name: '', parent_id: '', level: 1, description: '', commission_rate: 0, sort_order: 0 });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteCat = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete'); }
  };

  const handleBulkUpload = async () => {
    const lines = bulkCsv.trim().split('\n');
    if (lines.length < 2) { toast.error('Need header + data rows'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const items = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(',').map(v => v.trim());
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = vals[idx] || ''; });
      if (obj.parent) {
        const parent = categories.find(c => c.name.toLowerCase() === obj.parent.toLowerCase());
        if (parent) { obj.parent_id = parent.category_id; obj.level = 2; }
      }
      items.push(obj);
    }
    try {
      const res = await axios.post(`${API_URL}/api/admin/categories/bulk`, { categories: items }, { withCredentials: true });
      toast.success(`${res.data.created} of ${res.data.total} created`);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} errors`);
      setBulkDialogOpen(false);
      setBulkCsv('');
      fetchData();
    } catch (e) { toast.error('Bulk upload failed'); }
  };

  const handleSaveBrand = async () => {
    try {
      if (editingBrand) {
        await axios.patch(`${API_URL}/api/admin/brands/${editingBrand.brand_id}`, brandForm, { withCredentials: true });
        toast.success('Brand updated');
      } else {
        await axios.post(`${API_URL}/api/admin/brands`, brandForm, { withCredentials: true });
        toast.success('Brand created');
      }
      setBrandDialogOpen(false);
      setEditingBrand(null);
      setBrandForm({ name: '', description: '', requires_authorization: false });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteBrand = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/brands/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete'); }
  };

  const handleBulkBrands = async () => {
    const lines = bulkBrandCsv.trim().split('\n');
    if (lines.length < 2) { toast.error('Need header + data rows'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const items = lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim());
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = vals[idx] || ''; });
      if (obj.requires_authorization) obj.requires_authorization = obj.requires_authorization.toLowerCase() === 'true';
      return obj;
    });
    try {
      const res = await axios.post(`${API_URL}/api/admin/brands/bulk`, { brands: items }, { withCredentials: true });
      toast.success(`${res.data.created} of ${res.data.total} created`);
      setBulkBrandDialogOpen(false);
      setBulkBrandCsv('');
      fetchData();
    } catch (e) { toast.error('Bulk upload failed'); }
  };

  const filteredCats = categories.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()));
  const filteredBrands = brands.filter(b => !search || b.name.toLowerCase().includes(search.toLowerCase()));

  const catColumns = [
    { key: 'name', label: 'Name', render: (v, row) => (
      <div className="flex items-center gap-1">
        {row.level > 1 && <CaretRight size={12} className="text-[#9c9db7]" />}
        <span className={row.level === 1 ? 'font-semibold text-[#0A0A0A]' : 'text-[#3c3c64]'}>{v}</span>
      </div>
    )},
    { key: 'level', label: 'Level', render: v => <span className={`px-2 py-0.5 text-xs font-semibold rounded ${v === 1 ? 'bg-[#7d64ff]/10 text-[#7d64ff]' : 'bg-[#f0f0f0] text-[#5a5c87]'}`}>L{v}</span> },
    { key: 'commission_rate', label: 'Commission %', render: v => v ? `${v}%` : '-' },
    { key: 'is_active', label: 'Status', render: v => <span className={`px-2 py-0.5 text-xs font-semibold rounded ${v ? 'sp-badge-success' : 'sp-badge-neutral'}`}>{v ? 'Active' : 'Inactive'}</span> },
    { key: 'category_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" data-testid={`edit-cat-${v}`} onClick={() => { setEditingCat(row); setCatForm({ name: row.name, parent_id: row.parent_id || '', level: row.level, description: row.description || '', commission_rate: row.commission_rate || 0, sort_order: row.sort_order || 0 }); setCatDialogOpen(true); }}><Pencil size={15} /></Button>
        <Button variant="ghost" size="sm" className="text-[#ea4b5a]" data-testid={`delete-cat-${v}`} onClick={() => handleDeleteCat(v)}><Trash size={15} /></Button>
      </div>
    )}
  ];

  const brandColumns = [
    { key: 'name', label: 'Brand Name' },
    { key: 'requires_authorization', label: 'Auth Required', render: v => v ? <span className="px-2 py-0.5 text-xs font-semibold rounded bg-[#f6a609]/10 text-[#d18f08]">Yes</span> : <span className="text-xs text-[#9c9db7]">No</span> },
    { key: 'is_active', label: 'Status', render: v => <span className={`px-2 py-0.5 text-xs font-semibold rounded ${v ? 'sp-badge-success' : 'sp-badge-neutral'}`}>{v ? 'Active' : 'Inactive'}</span> },
    { key: 'brand_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" data-testid={`edit-brand-${v}`} onClick={() => { setEditingBrand(row); setBrandForm({ name: row.name, description: row.description || '', requires_authorization: row.requires_authorization || false }); setBrandDialogOpen(true); }}><Pencil size={15} /></Button>
        <Button variant="ghost" size="sm" className="text-[#ea4b5a]" data-testid={`delete-brand-${v}`} onClick={() => handleDeleteBrand(v)}><Trash size={15} /></Button>
      </div>
    )}
  ];

  return (
    <DashboardLayout title="Categories & Brands" subtitle="Manage product taxonomy and brand master data">
      <div className="mb-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5c87]" size={18} />
          <Input placeholder="Search categories or brands..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="taxonomy-search" />
        </div>
      </div>

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories ({categories.length})</TabsTrigger>
          <TabsTrigger value="brands" data-testid="tab-brands">Brands ({brands.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="categories">
          <div className="flex gap-2 mb-4">
            <Button onClick={() => { setEditingCat(null); setCatForm({ name: '', parent_id: '', level: 1, description: '', commission_rate: 0, sort_order: 0 }); setCatDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="add-category-btn">
              <Plus size={16} className="mr-1" /> Add Category
            </Button>
            <Button variant="outline" onClick={() => setBulkDialogOpen(true)} className="border-[#e5e5ea]" data-testid="bulk-categories-btn">
              <Upload size={16} className="mr-1" /> Bulk Upload
            </Button>
          </div>

          {/* Tree View */}
          <div className="bg-white border border-[#e5e5ea] rounded-xl p-4 mb-4">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9c9db7] mb-3">Category Hierarchy</p>
            {buildTree.map(root => (
              <div key={root.category_id} className="mb-2">
                <div className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[#fafafe]">
                  <TreeStructure size={16} className="text-[#7d64ff]" />
                  <span className="text-sm font-semibold text-[#0A0A0A]">{root.name}</span>
                  <span className="text-[10px] text-[#9c9db7] ml-1">({root.children.length} sub)</span>
                </div>
                {root.children.map(child => (
                  <div key={child.category_id} className="flex items-center gap-2 py-1 px-2 ml-8 rounded hover:bg-[#fafafe]">
                    <CaretRight size={12} className="text-[#d1d1d6]" />
                    <span className="text-[13px] text-[#3c3c64]">{child.name}</span>
                    {child.commission_rate > 0 && <span className="text-[10px] text-[#7d64ff]">{child.commission_rate}%</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <SortableTable columns={catColumns} data={filteredCats} pageSize={15} emptyMessage="No categories" emptyIcon={TreeStructure} />
        </TabsContent>

        <TabsContent value="brands">
          <div className="flex gap-2 mb-4">
            <Button onClick={() => { setEditingBrand(null); setBrandForm({ name: '', description: '', requires_authorization: false }); setBrandDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="add-brand-btn">
              <Plus size={16} className="mr-1" /> Add Brand
            </Button>
            <Button variant="outline" onClick={() => setBulkBrandDialogOpen(true)} className="border-[#e5e5ea]" data-testid="bulk-brands-btn">
              <Upload size={16} className="mr-1" /> Bulk Upload
            </Button>
          </div>
          <SortableTable columns={brandColumns} data={filteredBrands} pageSize={15} emptyMessage="No brands" />
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingCat ? 'Edit Category' : 'Add Category'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Name *</Label>
              <Input value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} className="mt-1 rounded-sm" data-testid="cat-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Parent Category</Label>
              <Select value={catForm.parent_id || "none"} onValueChange={v => setCatForm({ ...catForm, parent_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="cat-parent-select"><SelectValue placeholder="None (Top Level)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Top Level)</SelectItem>
                  {l1Categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Commission %</Label>
                <Input type="number" step="0.1" value={catForm.commission_rate} onChange={e => setCatForm({ ...catForm, commission_rate: e.target.value })} className="mt-1 rounded-sm" data-testid="cat-commission-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Sort Order</Label>
                <Input type="number" value={catForm.sort_order} onChange={e => setCatForm({ ...catForm, sort_order: e.target.value })} className="mt-1 rounded-sm" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Description</Label>
              <Textarea value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} rows={2} className="mt-1 rounded-sm" />
            </div>
            <div className="flex gap-4 pt-2">
              <Button variant="outline" onClick={() => setCatDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveCat} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="save-cat-btn" disabled={!catForm.name.trim()}>
                {editingCat ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Brand Dialog */}
      <Dialog open={brandDialogOpen} onOpenChange={setBrandDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingBrand ? 'Edit Brand' : 'Add Brand'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Brand Name *</Label>
              <Input value={brandForm.name} onChange={e => setBrandForm({ ...brandForm, name: e.target.value })} className="mt-1 rounded-sm" data-testid="brand-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Description</Label>
              <Textarea value={brandForm.description} onChange={e => setBrandForm({ ...brandForm, description: e.target.value })} rows={2} className="mt-1 rounded-sm" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={brandForm.requires_authorization} onChange={e => setBrandForm({ ...brandForm, requires_authorization: e.target.checked })} className="w-4 h-4 rounded border-[#e5e5ea]" data-testid="brand-auth-checkbox" />
              <span className="text-sm">Requires seller authorization to sell</span>
            </label>
            <div className="flex gap-4 pt-2">
              <Button variant="outline" onClick={() => setBrandDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveBrand} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="save-brand-btn" disabled={!brandForm.name.trim()}>
                {editingBrand ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Category Upload */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bulk Category Upload</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#fcfcfc] border border-[#e5e5ea] rounded text-sm">
              <p className="font-semibold mb-1">CSV Format:</p>
              <code className="text-xs block bg-white p-2 rounded border font-mono">name,parent,description,commission_rate<br/>Smartphones,Electronics,All smartphones,10<br/>T-Shirts,Fashion,Men and Women,18</code>
            </div>
            <Textarea value={bulkCsv} onChange={e => setBulkCsv(e.target.value)} rows={8} placeholder="Paste CSV..." className="font-mono text-xs rounded-sm" data-testid="bulk-cat-csv" />
            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleBulkUpload} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="bulk-cat-submit">Upload</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Brand Upload */}
      <Dialog open={bulkBrandDialogOpen} onOpenChange={setBulkBrandDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bulk Brand Upload</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#fcfcfc] border border-[#e5e5ea] rounded text-sm">
              <p className="font-semibold mb-1">CSV Format:</p>
              <code className="text-xs block bg-white p-2 rounded border font-mono">name,description,requires_authorization<br/>Zara,Fashion brand,false<br/>Gucci,Luxury brand,true</code>
            </div>
            <Textarea value={bulkBrandCsv} onChange={e => setBulkBrandCsv(e.target.value)} rows={8} placeholder="Paste CSV..." className="font-mono text-xs rounded-sm" data-testid="bulk-brand-csv" />
            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setBulkBrandDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleBulkBrands} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="bulk-brand-submit">Upload</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
